import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type OptionSelectItem = {
  value: string;
  label: string;
};

type OptionSelectProps = {
  value: string;
  options: readonly OptionSelectItem[];
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
};

const triggerClassName =
  'w-full h-8 px-2.5 text-xs font-normal rounded-md border bg-white outline-none transition flex items-center justify-between gap-2 text-left cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:opacity-60 disabled:cursor-not-allowed';

export function OptionSelect({
  value,
  options,
  onChange,
  disabled,
  id,
  className = '',
}: OptionSelectProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const selected = options.find((item) => item.value === value) ?? options[0];

  const toggle = () => {
    if (disabled) return;
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuHeight = Math.min(192, options.length * 32 + 8);
      const spaceBelow = window.innerHeight - rect.bottom;
      const top =
        spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8
          ? rect.top - menuHeight - 4
          : rect.bottom + 4;
      setMenuPos({ top, left: rect.left, width: rect.width });
    }
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      close();
    };
    window.addEventListener('resize', close);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  return (
    <div className={`w-full ${className}`.trim()}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        className={`${triggerClassName} ${open ? 'border-green-500' : 'border-gray-200'}`}
      >
        <span className="min-w-0 flex-1 truncate text-gray-800">{selected?.label ?? ''}</span>
        <i
          className={`ri-arrow-down-s-line text-gray-400 text-lg flex-shrink-0 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open
        ? createPortal(
            <>
              <div className="fixed inset-0 z-[110]" onClick={() => setOpen(false)} />
              <div
                ref={menuRef}
                role="listbox"
                aria-labelledby={id}
                className="fixed z-[111] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden py-1 max-h-48 overflow-y-auto"
                style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
              >
                {options.map((item) => {
                  const active = item.value === value;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        onChange(item.value);
                        setOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-xs cursor-pointer flex items-center justify-between gap-2 ${
                        active
                          ? 'bg-green-50 text-green-700 font-semibold'
                          : 'text-gray-700 font-normal hover:bg-gray-50'
                      }`}
                    >
                      <span className="truncate">{item.label}</span>
                      {active ? <i className="ri-check-line text-green-600 flex-shrink-0" /> : null}
                    </button>
                  );
                })}
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
}
